import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  TextField,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { streamAgentChat } from '../../services/agentClient';
import MessageList from './MessageList';
import ActionCard from './ActionCard';
import './AIAssistantPanel.css';

/**
 * Panel lateral (Drawer derecho) con el chat del agente NutriAgent.
 * En móvil ocupa la pantalla completa.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *
 * Estado:
 *  - messages: lista de { id, role, content, isError }
 *  - conversationId: hilo del agente (se conserva entre turnos)
 *  - loading: true mientras hay respuesta en streaming
 *  - activeTool: etiqueta legible de la tool en uso (o null)
 *  - pendingActions: { conversationId, actions } cuando el agente pausa esperando
 *    confirmación del usuario (human-in-the-loop), o null.
 *
 * Decisión sobre el composer mientras hay acción pendiente: se DESHABILITA la
 * entrada de texto hasta que el usuario decida (Aplicar / Cancelar). Es lo más
 * simple y coherente: el turno está pausado en el backend esperando una
 * reanudación, así que no tiene sentido permitir escribir otro mensaje.
 */

// Genera ids únicos y estables para los mensajes sin depender del índice.
let messageSeq = 0;
const nextId = () => `msg-${Date.now()}-${messageSeq++}`;

// Traduce el nombre técnico de una tool a un texto amable para el usuario.
const TOOL_LABELS = {
  menu: 'Consultando tu menú…',
  menu_week_detail: 'Consultando tu menú…',
  recipes: 'Buscando recetas…',
  recipes_search: 'Buscando recetas…',
  ingredients: 'Revisando ingredientes…',
  tracking: 'Revisando tu seguimiento…',
  shopping: 'Preparando tu lista de la compra…',
};

const toolLabel = (tool) => {
  if (!tool) return 'Trabajando…';
  return TOOL_LABELS[tool] || `Consultando ${tool}…`;
};

const AIAssistantPanel = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { authToken, logout } = useAuth();

  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTool, setActiveTool] = useState(null);
  // Acción(es) pendiente(s) de confirmación (human-in-the-loop), o null.
  const [pendingActions, setPendingActions] = useState(null);
  // true mientras se reanuda el hilo tras Aplicar/Cancelar (deshabilita la card).
  const [resuming, setResuming] = useState(false);

  // AbortController del turno en curso, para poder cancelar el streaming.
  const abortRef = useRef(null);
  // Guardamos el id del mensaje del asistente que se está rellenando.
  const assistantIdRef = useRef(null);

  // Añade (o concatena) texto al mensaje del asistente en curso.
  const appendToAssistant = useCallback((text) => {
    const id = assistantIdRef.current;
    if (!id) return;
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? { ...m, content: m.content + text } : m))
    );
  }, []);

  // Marca el mensaje del asistente en curso como error (o crea uno si no hay).
  const setAssistantError = useCallback((errText) => {
    const id = assistantIdRef.current;
    setMessages((prev) => {
      const existing = prev.find((m) => m.id === id);
      // Si el mensaje del asistente sigue vacío, lo reutilizamos como error.
      if (existing && !existing.content) {
        return prev.map((m) =>
          m.id === id ? { ...m, content: errText, isError: true } : m
        );
      }
      // Si ya tenía contenido, añadimos una burbuja de error aparte.
      return [
        ...prev,
        { id: nextId(), role: 'assistant', content: errText, isError: true },
      ];
    });
  }, []);

  // Handlers SSE compartidos por mensajes normales y reanudaciones. Se
  // construyen aquí para no duplicar la lógica de token/tool/done/error y la del
  // nuevo evento action_required.
  const buildStreamHandlers = useCallback(
    () => ({
      onToken: (text) => appendToAssistant(text),
      onToolStart: (tool) => setActiveTool(toolLabel(tool)),
      onToolEnd: () => setActiveTool(null),
      // El agente pausa esperando confirmación: guardamos las acciones y dejamos
      // de cargar. El "done" posterior cerrará el stream pero ya no spinneamos.
      onActionRequired: ({ conversationId: cid, actions }) => {
        if (cid) setConversationId(cid);
        setActiveTool(null);
        setPendingActions({ conversationId: cid, actions });
      },
      onDone: (newConversationId) => {
        if (newConversationId) setConversationId(newConversationId);
        setActiveTool(null);
        setLoading(false);
        setResuming(false);
        abortRef.current = null;
      },
      onError: (err) => {
        setActiveTool(null);
        setLoading(false);
        setResuming(false);
        abortRef.current = null;

        // Sesión caducada: avisamos y cerramos sesión.
        if (err?.code === 401) {
          setAssistantError(
            'Tu sesión ha caducado. Vuelve a iniciar sesión para seguir usando el asistente.'
          );
          if (logout) logout();
          return;
        }
        setAssistantError(
          err?.message || 'Ha ocurrido un error. Inténtalo de nuevo.'
        );
      },
    }),
    [appendToAssistant, setAssistantError, logout]
  );

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    // No se puede enviar si está cargando o hay una acción pendiente de decidir.
    if (!trimmed || loading || pendingActions) return;

    const userMsg = { id: nextId(), role: 'user', content: trimmed };
    const assistantId = nextId();
    assistantIdRef.current = assistantId;

    setMessages((prev) => [
      ...prev,
      userMsg,
      { id: assistantId, role: 'assistant', content: '' },
    ]);
    setInput('');
    setLoading(true);
    setActiveTool(null);

    const controller = new AbortController();
    abortRef.current = controller;

    const screenContext = { route: location.pathname };

    streamAgentChat(
      {
        authToken,
        message: trimmed,
        conversationId,
        screenContext,
        signal: controller.signal,
      },
      buildStreamHandlers()
    );
  }, [
    input,
    loading,
    pendingActions,
    authToken,
    conversationId,
    location.pathname,
    buildStreamHandlers,
  ]);

  // Reanuda el hilo pausado con la decisión del usuario (confirm true/false).
  const resolvePendingActions = useCallback(
    (confirm) => {
      if (!pendingActions || resuming) return;

      const cid = pendingActions.conversationId || conversationId;

      // Línea de sistema discreta reflejando la decisión.
      const noticeMsg = {
        id: nextId(),
        role: 'assistant',
        content: confirm ? '✓ Acción aplicada.' : 'Acción cancelada.',
      };
      // Burbuja del asistente nueva para la respuesta que llegue al reanudar.
      const assistantId = nextId();
      assistantIdRef.current = assistantId;

      setMessages((prev) => [
        ...prev,
        noticeMsg,
        { id: assistantId, role: 'assistant', content: '' },
      ]);

      setPendingActions(null);
      setResuming(true);
      setLoading(true);
      setActiveTool(null);

      const controller = new AbortController();
      abortRef.current = controller;

      streamAgentChat(
        {
          authToken,
          conversationId: cid,
          resume: { confirm },
          signal: controller.signal,
        },
        buildStreamHandlers()
      );
    },
    [pendingActions, resuming, conversationId, authToken, buildStreamHandlers]
  );

  const handleApply = useCallback(() => resolvePendingActions(true), [resolvePendingActions]);
  const handleCancelAction = useCallback(
    () => resolvePendingActions(false),
    [resolvePendingActions]
  );

  // Cancela la respuesta en streaming actual.
  const handleCancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setLoading(false);
    setActiveTool(null);
  }, []);

  // Enter envía; Shift+Enter inserta salto de línea.
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const drawerWidth = isMobile ? '100%' : 420;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        className: 'assistant-modern-drawer',
        sx: {
          width: drawerWidth,
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Cabecera */}
      <Box
        className="assistant-modern-header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: '#eef2ff',
            color: '#4f46e5',
          }}
        >
          <SmartToyOutlinedIcon fontSize="small" />
        </Box>
        <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, fontSize: '1.05rem' }}>
          Asistente
        </Typography>
        <IconButton onClick={onClose} aria-label="Cerrar asistente" size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Lista de mensajes */}
      <MessageList messages={messages} loading={loading} activeTool={activeTool} />

      {/* Tarjeta de confirmación de acciones (human-in-the-loop). */}
      {pendingActions && (
        <Box sx={{ px: 2, pt: 0.5, bgcolor: '#fff' }}>
          <ActionCard
            actions={pendingActions.actions}
            onApply={handleApply}
            onCancel={handleCancelAction}
            disabled={resuming}
          />
        </Box>
      )}

      {/* Composer */}
      <Box
        className="assistant-modern-composer"
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          p: 1.5,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#fff',
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={5}
          size="small"
          placeholder={
            pendingActions
              ? 'Confirma o cancela la acción para continuar…'
              : 'Escribe tu mensaje…'
          }
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading || Boolean(pendingActions)}
          inputProps={{ 'aria-label': 'Mensaje para el asistente' }}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#f8fafc' },
          }}
        />

        {loading ? (
          <Tooltip title="Cancelar respuesta">
            <span>
              <IconButton
                onClick={handleCancel}
                aria-label="Cancelar respuesta"
                sx={{
                  bgcolor: '#fee2e2',
                  color: '#dc2626',
                  '&:hover': { bgcolor: '#fecaca' },
                }}
              >
                <StopCircleOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Enviar">
            <span>
              <IconButton
                onClick={handleSend}
                aria-label="Enviar mensaje"
                disabled={!input.trim() || Boolean(pendingActions)}
                sx={{
                  bgcolor: '#4f46e5',
                  color: '#fff',
                  '&:hover': { bgcolor: '#4338ca' },
                  '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
                }}
              >
                <SendIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Drawer>
  );
};

export default AIAssistantPanel;
